import * as pathLib from 'path';
import type { SidebarItem, SidebarDataType, ProductHeaderDataType } from '../types';

export const localizeContentPath = (path: string, locale: string, version?: string) => {
  const [namespace, ...sharedContentPath] = path.split('/').filter(Boolean);

  let finalNamespace = namespace;
  if (namespace === 'internal') {
    finalNamespace = `internal/${sharedContentPath.shift()}`;
  }

  return version
    ? pathLib.join('/', finalNamespace, version, locale, ...sharedContentPath)
    : pathLib.join('/', finalNamespace, locale, ...sharedContentPath);
};

export function transformRelativeUrl(url: string, filePath: string): string {
  // Only handle relative links starting with ./ or ../
  if (!url.startsWith('./') && !url.startsWith('../')) {
    return url;
  }

  // Use path.resolve to resolve the filepath with the href
  const resolvedPath = pathLib.resolve('/', filePath, url);
  const pathParts = resolvedPath.split('/').filter(Boolean);

  // Extract namespace and path after 'pages'
  const [namespace, ...pathAfterNamespace] = pathParts;
  const pagesIndex = pathParts.indexOf('pages');
  const version = pathParts[pagesIndex - 2] === namespace ? undefined : pathParts[pagesIndex - 2];
  const restOfPath = pagesIndex !== -1 ? pathParts.slice(pagesIndex + 1) : pathAfterNamespace;
  // Build absolute path
  const parts = version
    ? [namespace, version, ...restOfPath].filter(Boolean)
    : [namespace, ...restOfPath].filter(Boolean);
  return `/${parts.join('/')}`;
}

/**
 * Transforms a URL by removing .mdx extension and resolving relative URLs.
 * This is used for frontmatter properties like redirectTo.
 *
 * @param url - The URL to transform
 * @param filePath - The file path used to resolve relative URLs
 * @param isIndex - Whether the current file is an index file
 * @returns The transformed URL
 */
export function transformFrontmatterUrl(url: string, filePath: string, isIndex: boolean): string {
  // Remove .mdx extension
  let transformed = url.replace(/\.mdx($|#|\?)/, '$1');

  // Transform relative URLs only for index files
  if (isIndex && (transformed.startsWith('./') || transformed.startsWith('../'))) {
    transformed = transformRelativeUrl(transformed, filePath);
  }

  return transformed;
}

/**
 * Transforms relative links in markdown/MDX content to absolute links.
 * Handles markdown links [text](url), JSX href attributes, and JSX src attributes.
 *
 * @param content - The markdown/MDX content string
 * @param sourceDirectory - The directory path of the content (used to resolve relative links)
 * @returns Content with relative links transformed to absolute links
 */
export function transformRelativeLinksInContent(content: string, sourceDirectory: string): string {
  // Transform markdown links: [text](./relative/path) or [text](../relative/path)
  let transformedContent = content.replace(/\[([^\]]+)\]\((\.\.?\/[^)]+)\)/g, (_match, text, url) => {
    const transformedUrl = transformRelativeUrl(url, sourceDirectory);
    return `[${text}](${transformedUrl})`;
  });

  // Transform JSX href attributes: href="./relative/path" or href="../relative/path"
  transformedContent = transformedContent.replace(/href=["'](\.\.?\/[^"']+)["']/g, (_match, url) => {
    const transformedUrl = transformRelativeUrl(url, sourceDirectory);
    return `href="${transformedUrl}"`;
  });

  // Transform JSX src attributes: src="./relative/path" or src="../relative/path"
  transformedContent = transformedContent.replace(/src=["'](\.\.?\/[^"']+)["']/g, (_match, url) => {
    const transformedUrl = transformRelativeUrl(url, sourceDirectory);
    return `src="${transformedUrl}"`;
  });

  return transformedContent;
}

/**
 * Recursively transform relative URLs in an AST by traversing nodes manually.
 * This function modifies the AST in place.
 *
 * @param node - The AST node to transform
 * @param includeDirectory - The directory path used to resolve relative links
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function transformRelativeUrlsInTree(node: any, includeDirectory: string): void {
  // Transform link and image nodes
  if ((node.type === 'link' || node.type === 'image') && node.url) {
    if (node.url.startsWith('./') || node.url.startsWith('../')) {
      node.url = transformRelativeUrl(node.url, includeDirectory);
    }
  }

  // Transform JSX element attributes
  if (node.type === 'mdxJsxTextElement' || node.type === 'mdxJsxFlowElement') {
    if (node.attributes) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      node.attributes.forEach((attr: any) => {
        if (
          (attr.name === 'href' || attr.name === 'src') &&
          typeof attr.value === 'string' &&
          (attr.value.startsWith('./') || attr.value.startsWith('../'))
        ) {
          attr.value = transformRelativeUrl(attr.value, includeDirectory);
        }
      });
    }
  }

  // Recursively process children
  if (node.children && Array.isArray(node.children)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    node.children.forEach((child: any) => transformRelativeUrlsInTree(child, includeDirectory));
  }
}

/**
 * Transforms all relative URLs in a sidebar data structure
 * @param sidebarData - The sidebar data to transform
 * @param filePath - The file path to use for resolving relative URLs
 * @returns The sidebar data with transformed URLs
 */
export function transformSidebarData(sidebarData: SidebarDataType, filePath: string): SidebarDataType {
  // If it's a $ref object, return it as-is (no transformation needed)
  if ('$ref' in sidebarData) {
    return sidebarData;
  }

  const transformSidebarItem = (item: SidebarItem): SidebarItem => {
    return {
      ...item,
      href: item.href ? transformRelativeUrl(item.href, filePath) : item.href,
      items: item.items.map(transformSidebarItem),
    };
  };

  return {
    ...sidebarData,
    parentLink: sidebarData.parentLink && {
      href: transformRelativeUrl(sidebarData.parentLink.href, filePath),
      title: sidebarData.parentLink.title,
    },
    sections: sidebarData.sections?.map(transformSidebarItem),
  };
}

/**
 * Transforms all relative URLs in a product header data structure
 * @param productHeaderData - The product header data to transform
 * @param filePath - The file path to use for resolving relative URLs
 * @returns The product header data with transformed URLs
 */
export function transformProductHeaderData(
  productHeaderData: ProductHeaderDataType,
  filePath: string,
): ProductHeaderDataType {
  return {
    ...productHeaderData,
    quickAccess: productHeaderData.quickAccess?.map((item) => ({
      ...item,
      href: item.href ? transformRelativeUrl(item.href, filePath) : item.href,
    })),
    callToAction: productHeaderData.callToAction
      ? {
          ...productHeaderData.callToAction,
          href: productHeaderData.callToAction.href
            ? transformRelativeUrl(productHeaderData.callToAction.href, filePath)
            : productHeaderData.callToAction.href,
        }
      : productHeaderData.callToAction,
  };
}
